if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get productId() {
        return this.getAttribute("data-product-id");
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector("quantity-input input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);

          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get("variant");

          if (variantId) {
            this.syncButtonStateWithVariant(this.currentVariant);
            return;
          }

          this.querySelectorAll("[data-variant-option]").forEach((input) => {
            input.checked = false;
          });
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;
        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || "main-product";

        const currentSelections = Array.from(
          this.querySelectorAll("[data-variant-option]:checked")
        ).map((input) => ({
          id: input.id,
          value: input.value,
        }));

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(
                productUrl,
                viewMode,
                currentSelections
              ),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector(
            "#MainProduct-quick-view__content"
          );
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById("shopify-section-sticky-atc-bar"),
              html.getElementById("shopify-section-sticky-atc-bar"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode, currentSelections = []) {
        return (html) => {
          const quickView = html.querySelector(
            "#MainProduct-quick-view__content"
          );
          if (quickView && viewMode === "quick-view") {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);

          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get("variant");

          if (variantId) {
            this.updateOptionValues(html);
          } else {
            this.updateOptionValues(html, currentSelections);
          }

          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`
            );

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === ""
          );
          updateSourceFromDestination("Badges", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("PricePerItem", ({ classList }) =>
            classList.contains("hidden")
          );
          updateSourceFromDestination("Volume");

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(
            `#QuantityRules-${this.dataset.section}`
          )?.classList.remove("hidden");
          this.querySelector(
            `#VolumeNote-${this.dataset.section}`
          )?.classList.remove("hidden");

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(
            `ProductSubmitButton-${this.sectionId}`
          );
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute("disabled") : true,
            FoxTheme.variantStrings.soldOut
          );

          FoxTheme.pubsub.publish(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            }
          );

          document.dispatchEvent(
            new CustomEvent("variant:changed", {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]"
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html, currentSelections = []) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          const newHtml = variantSelects.cloneNode(true);

          const urlParams = new URLSearchParams(window.location.search);
          const variantId = urlParams.get("variant");

          if (variantId) {
            HTMLUpdateUtility.viewTransition(
              this.variantSelectors,
              newHtml,
              this.preProcessHtmlCallbacks
            );
            return;
          }

          currentSelections.forEach((selection) => {
            const input = newHtml.querySelector(`#${selection.id}`);
            if (input && input.value === selection.value) {
              input.checked = true;
            }
          });

          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            newHtml,
            this.preProcessHtmlCallbacks
          );
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`
        );
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          )
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? "";
            input.dispatchEvent(new Event("change", { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
        if (!productMedia) return;

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === "function") {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true;
          }
          return false;
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(
          `ProductShare-${this.dataset.section}`
        );
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(
          `${window.shopUrl}${this.dataset.url}?variant=${variantId}`
        );
      }

      syncButtonStateWithVariant(variant) {
        if (!variant) return;

        const productForm = document.getElementById(
          `product-form-${this.dataset.section}`
        );
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton || !addButtonText) return;

        if (!variant.available) {
          addButton.setAttribute("disabled", "disabled");
          addButtonText.textContent = FoxTheme.variantStrings.soldOut;
        } else {
          addButton.removeAttribute("disabled");
          addButton.removeAttribute("data-select-option");

          addButtonText.textContent = `${FoxTheme.Currency.formatMoney(
            variant.price,
            FoxTheme.settings.moneyFormat
          )} • ${FoxTheme.variantStrings.addToCart}`;
        }

        const variantInput = productForm.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
        }

        const variantSelects = this.querySelector("variant-selects");
        if (variantSelects) {
          const optionInputs = variantSelects.querySelectorAll(
            "[data-variant-option]"
          );
          optionInputs.forEach((input) => {
            const optionPosition = parseInt(input.name.split("-")[1]) - 1;
            if (
              optionPosition >= 0 &&
              optionPosition < variant.options.length
            ) {
              input.checked = input.value === variant.options[optionPosition];
            }
          });
        }

        this.syncStickyATCButton(variant, !variant.available);
      }

      syncStickyATCButton(variant, isDisabled) {
        const stickyATCBar = document.querySelector(".sticky-atc-bar");
        if (!stickyATCBar) return;

        const stickyAddButton = stickyATCBar.querySelector('[name="add"]');
        const stickyAddButtonText = stickyATCBar.querySelector(
          '[name="add"] > span'
        );
        if (!stickyAddButton || !stickyAddButtonText) return;

        if (isDisabled) {
          stickyAddButton.setAttribute("disabled", "disabled");
          stickyAddButtonText.textContent = FoxTheme.variantStrings.soldOut;
        } else {
          stickyAddButton.removeAttribute("disabled");
          stickyAddButtonText.textContent = `${FoxTheme.Currency.formatMoney(
            variant.price,
            FoxTheme.settings.moneyFormat
          )} • ${FoxTheme.variantStrings.addToCart}`;
        }

        if (this.querySelector("[data-select-option]")) {
          stickyAddButton.setAttribute("data-select-option", "");
        } else {
          stickyAddButton.removeAttribute("data-select-option");
        }

        const stickyForm = stickyATCBar.querySelector("form");
        if (stickyForm) {
          const stickyVariantInput =
            stickyForm.querySelector('input[name="id"]');
          if (stickyVariantInput) {
            stickyVariantInput.value = variant.id;
          }

          const stickyVariantSelect = stickyForm.querySelector(
            ".sticky-atc-bar__variant-select"
          );
          if (stickyVariantSelect) {
            const option = stickyVariantSelect.querySelector(
              `option[value="${variant.id}"]`
            );
            if (option) {
              option.selected = true;
            }
          }
        }
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(
          `product-form-${this.dataset.section}`
        );
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');

        if (!addButton) return;

        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get("variant");
        const hasVariantInUrl = !!variantId;

        if (
          hasVariantInUrl &&
          this.currentVariant &&
          this.currentVariant.id.toString() === variantId
        ) {
          this.syncButtonStateWithVariant(this.currentVariant);
          return;
        }

        if (disable) {
          addButton.setAttribute("disabled", "disabled");
          if (text) {
            addButtonText.textContent = text;
          } else {
            const productOptions = document.querySelector("variant-selects");
            if (
              productOptions &&
              productOptions.querySelectorAll("[data-variant-option]").length >
                0
            ) {
              const firstOptionName =
                productOptions
                  .querySelector("[data-variant-option]")
                  ?.name?.split("-")[0] || "";
              addButtonText.textContent = FoxTheme.variantStrings.selectOptions;
            } else {
              addButtonText.textContent = FoxTheme.variantStrings.unavailable;
            }
          }
        } else {
          addButton.removeAttribute("disabled");
          const variant = this.getSelectedVariant(this);
          if (variant) {
            addButtonText.textContent = `${FoxTheme.Currency.formatMoney(
              variant.price,
              FoxTheme.settings.moneyFormat
            )} • ${FoxTheme.variantStrings.addToCart}`;
          }
        }

        if (productForm.querySelector("[data-select-option]")) {
          if (hasVariantInUrl) {
            addButton.removeAttribute("data-select-option");
          } else {
            addButton.setAttribute("data-select-option", "");
          }
        } else {
          addButton.removeAttribute("data-select-option");
        }

        const variantSelects = this.querySelector("variant-selects");
        if (variantSelects) {
          const selectedVariant = this.getSelectedVariant(this);
          if (selectedVariant) {
            const optionInputs = variantSelects.querySelectorAll(
              "[data-variant-option]"
            );
            optionInputs.forEach((input) => {
              const optionPosition = parseInt(input.name.split("-")[1]) - 1;
              if (
                optionPosition >= 0 &&
                optionPosition < selectedVariant.options.length
              ) {
                input.checked =
                  input.value === selectedVariant.options[optionPosition];
              }
            });

            this.syncStickyATCButton(selectedVariant, disable);
          }
        } else {
          const stickyATCBar = document.querySelector(".sticky-atc-bar");
          if (stickyATCBar) {
            const stickyAddButton = stickyATCBar.querySelector('[name="add"]');
            const stickyAddButtonText = stickyATCBar.querySelector(
              '[name="add"] > span'
            );

            if (stickyAddButton && stickyAddButtonText) {
              if (disable) {
                stickyAddButton.setAttribute("disabled", "disabled");
                stickyAddButtonText.textContent =
                  text || FoxTheme.variantStrings.unavailable;
              } else {
                stickyAddButton.removeAttribute("disabled");
                if (this.currentVariant) {
                  stickyAddButtonText.textContent = `${FoxTheme.Currency.formatMoney(
                    this.currentVariant.price,
                    FoxTheme.settings.moneyFormat
                  )} • ${FoxTheme.variantStrings.addToCart}`;
                }
              }

              if (productForm.querySelector("[data-select-option]")) {
                stickyAddButton.setAttribute("data-select-option", "");
              } else {
                stickyAddButton.removeAttribute("data-select-option");
              }
            }
          }
        }
      }

      setUnavailable() {
        const button = document.getElementById(
          `product-form-${this.dataset.section}`
        );
        const addButton = button.querySelector('[name="add"]');
        const addButtonText = button.querySelector('[name="add"] > span');
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(
          `Inventory-${this.dataset.section}`
        );
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (!addButton) return;
        addButtonText.textContent = FoxTheme.variantStrings.unavailable;
        if (price) price.classList.add("hidden");
        if (inventory) inventory.classList.add("hidden");
        if (sku) sku.classList.add("hidden");
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute("data-original-section")) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries,
          {
            data: {
              sectionId: this.sectionId,
              productId: this.productId,
            },
          }
        );
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(".quantity__rules-cart")?.classList.add(
          "btn--loading"
        );

        fetch(
          `${this.getAttribute(
            "data-url"
          )}?variant=${currentVariantId}&section_id=${this.sectionId}`
        )
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.updateQuantityRules(
              this.sectionId,
              this.productId,
              parsedHTML
            );
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector(".quantity__rules-cart")?.classList.remove(
              "btn--loading"
            );
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}
